import React, { useRef, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Clock, Calendar, Shield, DollarSign, Users, Mail, Phone, Camera, Scale } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface NightlifeTermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept?: () => void;
  packageType?: 'kampala' | 'jinja' | 'premium';
}

const NightlifeTermsModal: React.FC<NightlifeTermsModalProps> = ({ 
  isOpen, 
  onClose,
  onAccept,
  packageType = 'kampala'
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // We're intentionally NOT adding a click outside handler
  // to prevent the modal from closing when clicking outside
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const getTitle = () => {
    switch (packageType) {
      case 'jinja':
        return 'Jinja Night Crawling';
      case 'premium':
        return 'Premium VIP Experience';
      default:
        return 'Kampala Night Crawling';
    }
  };

  const handleAcceptClick = () => {
    if (onAccept) {
      onAccept();
    } else {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
            ref={modalRef}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-purple-600 mr-2" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Terms & Conditions – {getTitle()}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto p-4 max-h-[calc(90vh-8rem)]">
              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <DollarSign className="h-5 w-5 text-purple-600 mr-2" />
                    1. Booking & Payments
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Deposit Required:</strong> A 30% refundable fee 7 hours before {getTitle()} starts deposit secures your booking.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Payment Methods:</strong> Mobile money (MTN/Airtel).</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Full Payment Deadline:</strong> When we meet, cash is allowed.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Group Discounts:</strong> Groups of 4+ get 15% off (must book together).</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Clock className="h-5 w-5 text-purple-600 mr-2" />
                    2. Cancellations & Refunds
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>No Refunds for:</strong> After 2 hours of the 7 hour 30% payment, cancellations within 6 hours of the event, no-shows or late arrivals, removal due to misconduct (see Section 4).</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Rescheduling:</strong> Allowed 7+ days before the event (subject to availability).</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Force Majeure:</strong> Natural disasters, riots, or gov't closures result in credit only (no cash refunds).</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Users className="h-5 w-5 text-purple-600 mr-2" />
                    3. Age & ID Requirements
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Strict Age Policy:</strong> 21+ for alcohol-inclusive packages; 18+ for non-alcoholic.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Mandatory ID:</strong> Original passport/driver's license (photos not accepted).</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Shield className="h-5 w-5 text-purple-600 mr-2" />
                    4. Conduct & Safety
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Rules:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• No drugs (instant removal without refund).</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Aggressive behavior = banned from all future events.</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Dress code: Smart casual (no flip-flops in premium clubs).</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Security:</strong> Venues reserve the right to deny entry (no refunds).</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <DollarSign className="h-5 w-5 text-purple-600 mr-2" />
                    5. Alcohol Policy
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Controlled Service:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• 2 welcome drinks included; additional drinks at own cost.</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Bartenders may cut off intoxicated guests.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>BYOB?</strong> Not permitted—venue policies apply.</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Calendar className="h-5 w-5 text-purple-600 mr-2" />
                    6. Itinerary Changes
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>We May Adjust:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Venues due to private events or overcapacity.</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Transportation routes for safety.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Notification:</strong> Changes emailed/SMS'd 2+ hours before departure.</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Shield className="h-5 w-5 text-purple-600 mr-2" />
                    7. Liability Waiver
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Not Responsible For:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Lost phones/wallets (use lockers where available).</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Injuries from reckless behavior (e.g., drunken fights).</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Insurance Required:</strong> Proof of travel/medical insurance for international guests.</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Camera className="h-5 w-5 text-purple-600 mr-2" />
                    8. Media & Privacy
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Photos/Videos:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• May be used for marketing unless you opt-out in writing.</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <span>• Tag @travelcord.guide to share your experience!</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Data Protection:</strong> PCI-compliant payments; emails only used for booking confirmations.</span>
                    </li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <Scale className="h-5 w-5 text-purple-600 mr-2" />
                    9. Disputes & Governing Law
                  </h3>
                  <ul className="mt-3 space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Jurisdiction:</strong> Disputes resolved in Kampala courts under Ugandan law.</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span><strong>Contact:</strong></span>
                    </li>
                    <li className="flex items-start pl-7">
                      <Mail className="h-4 w-4 text-purple-600 mr-2" />
                      <span>info@travelcord.guide</span>
                    </li>
                    <li className="flex items-start pl-7">
                      <Phone className="h-4 w-4 text-purple-600 mr-2" />
                      <span>+256766830194 (response within 24 hours)</span>
                    </li>
                  </ul>
                </section>

                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-600">
                  <h3 className="font-semibold text-gray-900">Acceptance Clause</h3>
                  <p className="mt-2 text-gray-700">
                    "By booking, you confirm you've read, understood, and agreed to these Terms. Violations may result in removal without refund."
                  </p>
                </div>

                <div className="text-right text-sm text-gray-500">
                  Last Updated: 12/04/2025
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-end">
                <button
                  onClick={onClose}
                  className="py-2 px-4 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                {onAccept && (
                  <button
                    onClick={handleAcceptClick}
                    className="py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    I Understand & Accept
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default NightlifeTermsModal;